import useDashboardEditor from "MyDashboards/state/editor";
import { Element } from "MyDashboards/state/editorTypes";
import { scaledPx } from "MyDashboards/utils/px";
import * as CSS from "csstype";
import React from "react";
import { getLastSnapState, subscribe, unsubscribe } from "../Grid/grid";
import { TGridState } from "../Grid/types";

const GridLine = (props: { line: [number, number, number, number] }) => {
  const [x1, y1, x2, y2] = props.line;
  const commonProps: CSS.Properties = {
    stroke: "red",
    strokeWidth: "0.4",
    strokeDasharray: "5,5",
  };
  const commonProps2: CSS.Properties = {
    ...commonProps,
    stroke: "rgba(255,255,255, 0.5)",
  };
  return (
    <>
      <line x1={x1} y1={y1} x2={x2} y2={y2} style={commonProps} />
    </>
  );
};

const OverlayItems = (props: { width: number }) => {
  const activePage = useDashboardEditor((s) =>
    s.pages.find((p) => p.id === s.selectedPageId)
  );
  const activeElementId = useDashboardEditor((s) => s.selectedElementId);
  const acitveElements = useDashboardEditor((s) => s.selectedElements);
  const isDragging = useDashboardEditor((s) => s.isDragged);
  const dragInfo = useDashboardEditor((s) => s.dragInfo);
  const editorWidth = useDashboardEditor((s) => s.editorWidth);
  const [snapDelta, setSnapDelta] = React.useState<Partial<{
    x: number;
    y: number;
    width: number;
    height: number;
  }> | null>(null);

  const editorHeight = React.useMemo(() => {
    if (activePage) {
      return (editorWidth * activePage?.height) / activePage?.width;
    }
    return 1;
  }, [activePage, editorWidth]);

  React.useEffect(() => {
    const onNewState = (state: Partial<TGridState>) => {
      const { lastTransformResult } = state;
      if (lastTransformResult) {
        const { delta } = lastTransformResult;
        console.log("delta", delta);
        setSnapDelta(delta);
      }
    };
    subscribe(onNewState);
    return () => unsubscribe(onNewState);
  });
  const scaleFactor = React.useMemo(() => {
    if (activePage) {
      return props.width / activePage?.width;
    }
    return 1;
  }, [activePage]);

  const px = scaledPx(scaleFactor);

  const seletedElements = React.useMemo(() => {
    const ids = (() => {
      if (activeElementId) {
        return [activeElementId];
      }
      return acitveElements;
    })();
    return ids
      .map((id) => activePage?.elements.find((e) => e.id === id))
      .filter((v) => v);
  }, [activeElementId, acitveElements, activePage?.elements]);

  const getDragInfo = (el: Element) => {
    const startX = dragInfo?.startX || 0;
    const startY = dragInfo?.startY || 0;
    const x = dragInfo?.x || 0;
    const y = dragInfo?.y || 0;
    const dx = x - startX;
    const dy = y - startY;
    const width = dragInfo?.width || el.width;
    const height = dragInfo?.height || el.height;

    console.log("getDragInfo", x, startX, el.x + x);
    return {
      startX,
      startY,
      x: el.x + dx,
      y: el.y + dy,
      dx,
      dy,
      width,
      height,
    };
  };

  const snapState = getLastSnapState();

  return (
    <div style={{ position: "relative" }}>
      <div
        style={{
          position: "absolute",
          left: "0px",
          top: "0px",
          pointerEvents: "none",
          zIndex: 1000,
        }}
      >
        {activePage?.elements.map((el) => {
          const isSelected =
            el.id === activeElementId || acitveElements.includes(el.id);
          return (
            <div
              key={el.id}
              style={{
                pointerEvents: "none",
                position: "absolute",
                left: px(el.x),
                top: px(el.y),
                width: px(el.width),
                height: px(el.height),
                backgroundImage: `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='5' ry='5' stroke='%23ffffff' stroke-width='1.1546px' stroke-dasharray='5.77%2c 5.77' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e")`,
                borderRadius: "5.77px",
                boxShadow: isDragging ? "0px 0px 1px blue" : "none",
                filter: "invert(20%)",
              }}
            />
          );
        })}
        {isDragging && seletedElements && dragInfo ? (
          <>
            {seletedElements.map((el) => {
              if (!el) return null;
              let { dx, dy, x, y, width, height } = getDragInfo(el);
              let hadSnap = false;
              if (snapDelta && Object.keys(snapDelta).length > 0) {
                dx += snapDelta.x || 0;
                dy += snapDelta.y || 0;
                height += snapDelta.height || 0;
                width += snapDelta.width || 0;
                hadSnap = true;
              }

              return (
                <div
                  key={`holder-${el.id}`}
                  style={{
                    pointerEvents: "none",
                    position: "absolute",
                    left: px(el.x + dx),
                    top: px(el.y + dy),
                    width: px(width),
                    height: px(height),
                    background: hadSnap
                      ? "rgba(100,200,111,0.5)"
                      : "rgba(100,200,111,0.1)",
                    filter: "invert(20%)",
                    boxShadow: "0px 0px 3px blue",
                  }}
                />
              );
            })}
          </>
        ) : null}

        {snapState.lastTransformResult && snapState.state === "running" ? (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox={`0 0 ${editorWidth} ${editorHeight}`}
            width={px(editorWidth)}
            height={px(editorHeight)}
          >
            {snapState.lastTransformResult.results.map((r) => {
              // filter: 'invert(20%)'
              const commonProps: CSS.Properties = {
                filter: "invert(20%)",
                fill: "white",
                stroke: "rgb(50,0,0)",
                strokeWidth: "0.4",
                strokeDasharray: "5,5",
                boxShadow: "0px 0px 1px black",
              };
              return (
                <g key={JSON.stringify(r.delta)}>
                  {r.grids.map((grid) => {
                    if (grid.type === "top") {
                      return grid.gridItems.map((item) => (
                        <GridLine line={[0, item.top, editorWidth, item.top]} />
                      ));
                    }
                    if (grid.type === "vcenter") {
                      return grid.gridItems.map((item) => (
                        <GridLine
                          line={[0, item.vcenter, editorWidth, item.vcenter]}
                        />
                      ));
                    }
                    if (grid.type === "bottom") {
                      return grid.gridItems.map((item) => (
                        <GridLine
                          line={[0, item.bottom, editorWidth, item.bottom]}
                        />
                      ));
                    }

                    if (grid.type === "left") {
                      return grid.gridItems.map((item) => (
                        <GridLine
                          line={[item.left, 0, item.left, editorHeight]}
                        />
                      ));
                    }

                    if (grid.type === "hcenter") {
                      return grid.gridItems.map((item) => (
                        <GridLine
                          line={[item.hcenter, 0, item.hcenter, editorHeight]}
                        />
                      ));
                    }

                    if (grid.type === "right") {
                      return grid.gridItems.map((item) => (
                        <GridLine
                          line={[item.right, 0, item.right, editorHeight]}
                        />
                      ));
                    }

                    if (grid.type === "distLeft" && snapState.item) {
                      let activeItem = snapState.item;
                      const { lastTransformResult } = snapState;
                      let dx = 0;
                      let dy = 0;
                      if (lastTransformResult) {
                        const { delta } = lastTransformResult;
                        if (delta) {
                          dx = delta.x || 0;
                          dy = delta.y || 0;
                        }
                      }
                      return grid.gridItems.map((item, idx, arr) => {
                        if (idx > 0) {
                          dx = 0;
                          dy = 0;
                          activeItem = arr[idx - 1];
                        }
                        return (
                          <>
                            <line
                              key={`conn_${activeItem.id}_1`}
                              x1={item.right + 1}
                              y1={item.top - 10}
                              x2={activeItem.left - 1 + dx}
                              y2={item.top - 10}
                              stroke="red"
                              strokeWidth={0.4}
                            />
                            <line
                              key={`conn_${activeItem.id}_2`}
                              x1={activeItem.left - 1 + dx}
                              y1={item.top - 10}
                              x2={activeItem.left - 1 + dx}
                              y2={item.top}
                              stroke="red"
                              strokeWidth={0.4}
                            />
                            <line
                              key={`conn_${activeItem.id}_3`}
                              x1={item.right + 1}
                              y1={item.top - 10}
                              x2={item.right + 1}
                              y2={item.top}
                              stroke="red"
                              strokeWidth={0.4}
                            />
                          </>
                        );
                      });
                    }

                    return null;
                  })}
                </g>
              );
            })}
          </svg>
        ) : null}
      </div>
    </div>
  );
};

const Overlay = (props: { width: number }) => {
  const activePage = useDashboardEditor((s) => s.selectedPageId);
  if (!activePage) return null;
  return <OverlayItems width={props.width} />;
};

export default Overlay;
